import React, { useState, useEffect } from "react";
import {
  MessageSquare,
  Send,
  ArrowDownLeft,
  Target,
  UserCheck,
  TrendingUp,
  Zap,
  Award,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Gift,
  Tag,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Settings,
  MoreVertical,
  Users,
  Check,
  ArrowRight,
  Crown,
  X,
  Star,
  CheckCircle,
  CreditCard,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getUserSubscription, getPlans } from "../apis/subscription";
import SubscriptionModal from "../components/SubscriptionModal";
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";

const Dashboard = () => {
  const [currentOfferIndex, setCurrentOfferIndex] = useState(0);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);
  const [trialPlan, setTrialPlan] = useState(null);
  const [showPlansModal, setShowPlansModal] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const navigate = useNavigate();

  // Load subscription data
  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      setIsLoadingSubscription(true);
      const [subscriptionResponse, plansResponse] = await Promise.all([
        getUserSubscription(),
        getPlans(),
      ]);

      if (subscriptionResponse.data.success && subscriptionResponse.data.data) {
        const subscription = subscriptionResponse.data.data;
        const expiresAt = new Date(subscription.expires_at);
        const isExpired = expiresAt < new Date();
        
        if (!isExpired) {
          setCurrentSubscription(subscription);
        } else {
          setCurrentSubscription(null);
        }
      } else {
        setCurrentSubscription(null);
      }

      if (plansResponse.data.success) {
        const allPlans = plansResponse.data.data;
        const hasUsedTrialValue = plansResponse.data.has_used_trial || false;
        setHasUsedTrial(hasUsedTrialValue);
        const trial = allPlans.find(p => p.plan_name === 'trial');
        setTrialPlan(trial);
      }
    } catch (error) {
      console.error("Error loading subscription data:", error);
      setCurrentSubscription(null);
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  const handleSubscriptionSuccess = () => {
    loadSubscriptionData();
    setShowPlansModal(false);
  };

  const [analyticsData] = useState({
    statistics: {
      total_messages: 12500,
      sent_count: 11500,
      failed_count: 1000,
      total_incoming_messages: 3000,
      success_rate: 92,
    },
    day_wise: [
      { date: "Mon", sent_count: 1500, failed_count: 100 },
      { date: "Tue", sent_count: 1600, failed_count: 150 },
      { date: "Wed", sent_count: 1400, failed_count: 120 },
      { date: "Thu", sent_count: 1700, failed_count: 130 },
      { date: "Fri", sent_count: 1800, failed_count: 200 },
      { date: "Sat", sent_count: 2000, failed_count: 150 },
      { date: "Sun", sent_count: 1500, failed_count: 150 },
    ],
  });

  // Campaign Performance Data
  const campaignStats = [
    {
      name: "Broadcast Sent",
      current: 163,
      total: 512.0,
      percentage: 68,
      unit: "K",
      color: "#eefa94",
    },
    {
      name: "Active Campaigns",
      current: 780,
      total: 1000,
      percentage: 82,
      unit: "",
      color: "#e9eeea",
    },
  ];

  // Trips & Tracks Data with proper structure for overlapping bars
  const tripsData = [
    { month: "27 Jun", operations: 0.9, transfer: 0.45 },
    { month: "28 Jun", operations: 0.6, transfer: 0.32 },
    { month: "29 Jun", operations: 0.8, transfer: 0.25 },
    { month: "30 Jun", operations: 0.15, transfer: 0 },
    { month: "1 Jul", operations: 0.95, transfer: 0.35 },
    { month: "2 Jul", operations: 0.2, transfer: 0 },
    { month: "3 Jul", operations: 0.72, transfer: 0.52 },
    { month: "4 Jul", operations: 0.62, transfer: 0.28 },
  ];

  // Offers Carousel Data
  const offers = [
    {
      title: "Take Your WhatsApp Broadcasting to the Next Level",
      buttonText: "Upgrade",
      gradient: "from-gray-800 via-gray-900 to-black",
      image:
        "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=400&fit=crop",
    },
  ];

  const messageVolumeData = analyticsData?.day_wise?.map((d) => ({
    name: d.date,
    messages: d.sent_count + d.failed_count,
  }));

  const engagementVolumeData = [
    { name: "Mon", unique_users: 400, avg_score: 65 },
    { name: "Tue", unique_users: 300, avg_score: 70 },
    { name: "Wed", unique_users: 200, avg_score: 68 },
    { name: "Thu", unique_users: 278, avg_score: 75 },
    { name: "Fri", unique_users: 189, avg_score: 72 },
    { name: "Sat", unique_users: 239, avg_score: 78 },
    { name: "Sun", unique_users: 80, avg_score: 82 },
  ];

  const messageStats = [
    {
      label: "Total Messages",
      value: analyticsData.statistics.total_messages.toLocaleString(),
      change: "+12.5%",
      isPositive: true,
    },
    {
      label: "Messages Sent",
      value: analyticsData.statistics.sent_count.toLocaleString(),
      change: "+8.3%",
      isPositive: true,
    },
    {
      label: "Messages Received",
      value: analyticsData.statistics.total_incoming_messages.toLocaleString(),
      change: "+15.2%",
      isPositive: true,
    },
    {
      label: "Failed Count",
      value: analyticsData.statistics.failed_count.toLocaleString(),
      change: "-3.1%",
      isPositive: false,
    },
    {
      label: "Success Rate",
      value: analyticsData.statistics.success_rate + "%",
      change: "+2.4%",
      isPositive: true,
    },
  ];

  // Auto-rotate offers
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentOfferIndex((prev) => (prev + 1) % offers.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextOffer = () => {
    setCurrentOfferIndex((prev) => (prev + 1) % offers.length);
  };

  const prevOffer = () => {
    setCurrentOfferIndex(
      (prev) => (prev - 1 + offers.length) % offers.length
    );
  };

  // Render pill indicators
  const renderPillIndicators = (total, filled) => {
    const pills = [];
    for (let i = 0; i < total; i++) {
      pills.push(
        <div
          key={i}
          className={`h-8 rounded-full transition-all ${
            i < filled ? "bg-gray-900 w-12" : "bg-gray-300 w-12 opacity-40"
          }`}
        />
      );
    }
    return pills;
  };

  // Custom bar shape component for rounded bars
  const RoundedBar = (props) => {
    const { fill, x, y, width, height } = props;
    const radius = 20;

    if (height <= 0) return null;

    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          rx={radius}
          ry={radius}
        />
        <circle cx={x + width / 2} cy={y} r={4} fill="white" />
      </g>
    );
  };

  return (
    <div className="min-h-screen bg-[#ffff]">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid - Reduced Size */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          {messageStats.map((stat, index) => (
            <div
              key={index}
              className="rounded-2xl p-4 border border-gray-200 hover:shadow-sm transition-shadow"
              style={{ backgroundColor: "#e9eeea" }}
            >
              <p className="text-xs text-gray-600 mb-1.5 font-medium">
                {stat.label}
              </p>
              <p className="text-xl font-bold text-gray-900 mb-2">
                {stat.value}
              </p>
              <div className="flex items-center gap-1">
                {stat.isPositive ? (
                  <ArrowUpRight className="text-emerald-600" size={12} />
                ) : (
                  <ArrowDownRight className="text-rose-600" size={12} />
                )}
                <span
                  className={`text-xs font-semibold ${
                    stat.isPositive ? "text-emerald-600" : "text-rose-600"
                  }`}
                >
                  {stat.change}
                </span>
                <span className="text-xs text-gray-500">vs last week</span>
              </div>
            </div>
          ))}
        </div>

        {/* Campaign Performance & Upgrade Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
          {/* Active Campaigns Card */}
          <div
            className="rounded-3xl p-6 border border-gray-200 relative overflow-hidden"
            style={{ backgroundColor: "#e9eeea" }}
          >
            <div className="flex items-start justify-between mb-6">
              <div className="p-2.5 bg-white rounded-xl">
                <Zap className="text-gray-700" size={20} />
              </div>
              <button className="p-1 hover:bg-white/50 rounded-lg transition-colors">
                <MoreVertical className="text-gray-600" size={18} />
              </button>
            </div>

            <h3 className="text-sm font-medium text-gray-600 mb-2">
              Active Campaigns
            </h3>

            <div className="flex items-end gap-2 mb-1">
              <span className="text-5xl font-bold text-gray-900">
                {campaignStats[1].current}
              </span>
              <span className="text-lg text-gray-500 mb-1.5">
                / {campaignStats[1].total}
              </span>
            </div>

            <div className="flex items-center gap-2 mb-6">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/60 rounded-full">
                <span className="text-xs font-semibold text-gray-700">
                  {campaignStats[1].percentage}%
                </span>
              </div>
            </div>

            <div className="flex gap-2">{renderPillIndicators(7, 5)}</div>
          </div>

          {/* Broadcast Sent Card */}
          <div
            className="rounded-3xl p-6 border border-gray-200 relative overflow-hidden"
            style={{ backgroundColor: "#eefa94" }}
          >
            <div className="flex items-start justify-between mb-6">
              <div className="p-2.5 bg-white rounded-xl">
                <Send className="text-gray-700" size={20} />
              </div>
              <button className="p-1 hover:bg-white/50 rounded-lg transition-colors">
                <MoreVertical className="text-gray-600" size={18} />
              </button>
            </div>

            <h3 className="text-sm font-medium text-gray-600 mb-2">
              Broadcast Sent
            </h3>

            <div className="flex items-end gap-2 mb-1">
              <span className="text-5xl font-bold text-gray-900">
                {campaignStats[0].current}
              </span>
              <span className="text-lg text-gray-500 mb-1.5">
                / {campaignStats[0].total} {campaignStats[0].unit}
              </span>
            </div>

            <div className="flex items-center gap-2 mb-6">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/60 rounded-full">
                <span className="text-xs font-semibold text-gray-700">
                  {campaignStats[0].percentage}%
                </span>
              </div>
            </div>

            <div className="flex gap-2">{renderPillIndicators(7, 4)}</div>
          </div>

          {/* Subscription Card */}
          {!isLoadingSubscription && (
            <>
              {/* Trial Offer Card - Show if user hasn't used trial */}
              {!hasUsedTrial && trialPlan && !currentSubscription && (
                <div
                  className="rounded-3xl p-6 border border-gray-200 relative overflow-hidden"
                  style={{ backgroundColor: "#dbeafe" }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2.5 bg-white rounded-xl">
                      <Star className="text-blue-600" size={20} />
                    </div>
                    <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full">
                      Limited Time
                    </span>
                  </div>

                  <h3 className="text-sm font-medium text-gray-600 mb-2">
                    Start with Trial
                  </h3>

                  <div className="mb-4">
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-4xl font-bold text-gray-900">
                        ₹500
                      </span>
                      <span className="text-sm text-gray-600">/month</span>
                    </div>
                    <p className="text-xs text-gray-700 font-medium leading-relaxed">
                      Try all features for one month • Full access to everything
                    </p>
                  </div>

                  <button
                    onClick={() => setShowPlansModal(true)}
                    className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 shadow-md"
                  >
                    Get Started
                    <ArrowRight size={16} />
                  </button>
                </div>
              )}

              {/* Active Subscription Card - Show if user has active subscription */}
              {currentSubscription && (
                <div
                  className="rounded-3xl p-6 border border-gray-200 relative overflow-hidden"
                  style={{ backgroundColor: "#e9eeea" }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2.5 bg-white rounded-xl">
                      <Crown className="text-purple-600" size={20} />
                    </div>
                    <span className="px-3 py-1 bg-purple-600 text-white text-xs font-bold rounded-full">
                      Active
                    </span>
                  </div>

                  <h3 className="text-sm font-medium text-gray-600 mb-2">
                    Current Plan
                  </h3>

                  <div className="mb-4">
                    <p className="text-2xl font-bold text-gray-900 mb-2">
                      {currentSubscription.plan_display_name}
                    </p>
                    <p className="text-xs text-gray-600 font-medium">
                      Expires: {new Date(currentSubscription.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>

                  <button
                    onClick={() => setShowPlansModal(true)}
                    className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 shadow-md"
                  >
                    Upgrade / Renew
                    <ArrowRight size={16} />
                  </button>
                </div>
              )}

              {/* No Subscription Card - Show if no active subscription and trial used */}
              {!currentSubscription && hasUsedTrial && (
                <div
                  className="rounded-3xl p-6 border border-gray-200 relative overflow-hidden"
                  style={{ backgroundColor: "#e9eeea" }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2.5 bg-white rounded-xl">
                      <CreditCard className="text-gray-700" size={20} />
                    </div>
                    <span className="px-3 py-1 bg-gray-700 text-white text-xs font-bold rounded-full">
                      Subscribe
                    </span>
                  </div>

                  <h3 className="text-sm font-medium text-gray-600 mb-2">
                    Subscription
                  </h3>

                  <div className="mb-4">
                    <p className="text-lg font-bold text-gray-900 mb-2">
                      No Active Plan
                    </p>
                    <p className="text-xs text-gray-600 font-medium">
                      Subscribe to unlock all features
                    </p>
                  </div>

                  <button
                    onClick={() => setShowPlansModal(true)}
                    className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 shadow-md"
                  >
                    View Plans
                    <ArrowRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Statistics Chart with Overlapping Bars */}
        <div
          className="rounded-3xl p-6 border border-gray-200 mb-6"
          style={{ backgroundColor: "#e9eeea" }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-xl">
                <TrendingUp className="text-gray-700" size={18} />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Statistics</h3>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-900"></div>
                  <span className="text-sm text-gray-700 font-medium">
                    Campaigns
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: "#eefa94" }}
                  ></div>
                  <span className="text-sm text-gray-700 font-medium">
                    Broadcast
                  </span>
                </div>
              </div>
              <select className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-gray-900">
                <option>2025</option>
                <option>2024</option>
              </select>
            </div>
          </div>

          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={tripsData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#d1d5db"
                  vertical={false}
                />
                <XAxis
                  dataKey="month"
                  stroke="#6b7280"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="#6b7280"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, 1]}
                  ticks={[0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  }}
                  cursor={{ fill: "transparent" }}
                />
                {/* Background (larger) bar - Campaigns */}
                <Bar
                  dataKey="operations"
                  fill="#1f2937"
                  shape={<RoundedBar />}
                  barSize={28}
                />
                {/* Foreground (smaller) bar - Broadcast */}
                <Bar
                  dataKey="transfer"
                  fill="#eefa94"
                  shape={<RoundedBar />}
                  barSize={28}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Label Annotations */}
          <div className="mt-4 flex justify-center gap-8">
            <div className="text-center">
              <div className="inline-block px-3 py-1 bg-gray-900 text-white text-xs font-semibold rounded-full mb-1">
                87%
              </div>
            </div>
            <div className="text-center">
              <div className="inline-block px-3 py-1 bg-gray-900 text-white text-xs font-semibold rounded-full mb-1">
                32%
              </div>
            </div>
          </div>
        </div>

        {/* Additional Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Message Volume Chart */}
          <div
            className="rounded-3xl p-6 border border-gray-200"
            style={{ backgroundColor: "#e9eeea" }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-white rounded-xl">
                <MessageSquare className="text-gray-700" size={18} />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                Message Volume Trend
              </h3>
            </div>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={messageVolumeData}>
                  <defs>
                    <linearGradient id="msgFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1f2937" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#1f2937" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#d1d5db"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    stroke="#6b7280"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#6b7280"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="messages"
                    stroke="#1f2937"
                    strokeWidth={2}
                    fill="url(#msgFill)"
                    name="Messages"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Engagement Chart */}
          <div
            className="rounded-3xl p-6 border border-gray-200"
            style={{ backgroundColor: "#e9eeea" }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-white rounded-xl">
                <UserCheck className="text-gray-700" size={18} />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                User Engagement Metrics
              </h3>
            </div>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={engagementVolumeData}>
                  <defs>
                    <linearGradient id="engUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1f2937" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#1f2937" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="engScore" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#eefa94" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#eefa94" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#d1d5db"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="name"
                    stroke="#6b7280"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#6b7280"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="unique_users"
                    stroke="#1f2937"
                    fill="url(#engUsers)"
                    strokeWidth={2}
                    name="Unique Users"
                  />
                  <Area
                    type="monotone"
                    dataKey="avg_score"
                    stroke="#c5d930"
                    fill="url(#engScore)"
                    strokeWidth={2}
                    name="Avg Score"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showPlansModal}
        onClose={() => setShowPlansModal(false)}
        onSuccess={handleSubscriptionSuccess}
        allowClose={true}
      />
    </div>
  );
};

export default Dashboard;
